<script setup lang="ts">
import { computed, ref } from 'vue';
import { useElementSize } from '@vueuse/core';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type {
  AccommodationProductCardData,
  AlternativeHotel,
  AvailableItineraryAccommodationSegment,
} from '@apps/main/new-desktop/pages/tripOverview/models';
import { useTripOverviewPanelsStore } from '@apps/main/new-desktop/pages/tripOverview/stores/tripOverviewPanelsStore';
import { DisneylandCampaignBanner } from '@apps/main/new-desktop/modules/disneylandCampaign';
import type { DisneylandCampaignBannerData } from '@apps/main/new-desktop/core/models/DisneylandCampaign';

import {
  AppArrowScroller,
  AppButton,
  SemboStarIllustration,
  SemboHeartIllustration,
  AppAutoplayGallery,
  AppLazyLoadComponent,
} from '@libs/ui';

import DateCardTag from '../DateCardTag.vue';

import DesktopProductCardLayout from './DesktopProductCardLayout.vue';
import AccommodationProductCardContent from './AccommodationProductCardContent.vue';
import AlternativeAccommodationCardContent from './AlternativeAccommodationCardContent.vue';
import AccommodationPrice from './AccommodationPrice.vue';
import AccommodationCardSelectedFooter from './AccommodationCardSelectedFooter.vue';

interface AccommodationCarouselDesktopProps {
  segment: AvailableItineraryAccommodationSegment;
  printMode: boolean;
  isMetaExperiment: boolean;
  disneylandCampaignBannerData?: DisneylandCampaignBannerData;
  shouldShowDisneylandCampaignBanner?: boolean;
}

const props = defineProps<AccommodationCarouselDesktopProps>();

interface AccommodationCarouselDesktopEmits {
  (event: 'open-modal'): void;
}
const emit = defineEmits<AccommodationCarouselDesktopEmits>();

const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const coreStore = useCoreStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();

const activeAutoplayGalleryIndex = ref<number | null>(null);
const firstCardRef = ref();
const firstCardSize = useElementSize(firstCardRef);

const hotelCards = computed(() => {
  if (props.printMode) {
    return [props.segment.productCardData];
  }

  return [
    props.segment.productCardData,
    ...props.segment.alternativeHotels,
    null,
  ];
});

function showDetails(
  hotel: AlternativeHotel | AccommodationProductCardData,
  context: {
    isAlternative: boolean;
    shouldScrollToRooms: boolean;
  }
): void {
  if (props.printMode) {
    return;
  }
  const hotelCode = isSelectedHotel(hotel)
    ? props.segment.context.hotelCode
    : hotel.hotelCode;

  if (context.isAlternative) {
    statsigEventsStore.isAlternativeHotelOpened = true;
    statsigEventsStore.logTripOverviewAlternativeHotelsCardClicked();
  }
  if (
    (isSelectedHotel(hotel) && hotel.productCardContent.guestRating) ||
    (!isSelectedHotel(hotel) && hotel.guestRating)
  ) {
    statsigEventsStore.logHotelCardClickedWithRating();
  }

  tripOverviewPanelsStore.openHotelDetailsPanel(
    hotelCode,
    props.segment.context.ingredientId,
    context.shouldScrollToRooms
  );
}

function openHotelSelection(): void {
  statsigEventsStore.logTripOverviewAlternativeHotelsHotelSelectionCardClicked();
  routerStore.navigateToHotelSelection(props.segment.context.ingredientIndex);
}

function isSelectedHotel(
  hotel: AlternativeHotel | AccommodationProductCardData
): hotel is AccommodationProductCardData {
  return !!hotel['productCardContent'];
}
</script>

<template>
  <div
    :class="[
      'accommodation-products-carousel-desktop',
      { 'print-mode': props.printMode },
    ]"
  >
    <AppArrowScroller
      :items="hotelCards"
      :item-key-selector="
        (hotel, index) =>
          hotel
            ? isSelectedHotel(hotel)
              ? hotel.productCardContent.header
              : hotel.hotelName
            : index
      "
      :arrow-vertical-position="firstCardSize.height.value / 2"
      :display-dashed-border="false"
      :print-mode="props.printMode"
      :left-arrow-aria-label="$t('a11y.left_arrow_previous_suggestions')"
      :right-arrow-aria-label="$t('a11y.right_arrow_next_suggestions')"
    >
      <template #item="{ item: accommodationItem, index }">
        <button
          v-if="accommodationItem && isSelectedHotel(accommodationItem)"
          class="card"
          @click="
            showDetails(accommodationItem, {
              isAlternative: false,
              shouldScrollToRooms: false,
            })
          "
        >
          <DesktopProductCardLayout
            ref="firstCardRef"
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgSrcs"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="coreStore.handleImageLoadError"
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AccommodationProductCardContent
                :product-card-content="accommodationItem.productCardContent"
                :context="props.segment.context"
                :print-mode="props.printMode"
                @show-details="
                  showDetails(accommodationItem, {
                    isAlternative: false,
                    shouldScrollToRooms: false,
                  })
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: false,
                    shouldScrollToRooms: true,
                  })
                "
              />
            </template>
            <template #footer>
              <AccommodationCardSelectedFooter
                :ingredient-index="props.segment.context.ingredientIndex"
                :is-meta-experiment="props.isMetaExperiment"
              />
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>
        </button>
        <button
          v-if="
            accommodationItem &&
            !isSelectedHotel(accommodationItem) &&
            !props.printMode
          "
          class="card"
          @click="
            showDetails(accommodationItem, {
              isAlternative: true,
              shouldScrollToRooms: false,
            })
          "
        >
          <DesktopProductCardLayout
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgUrls"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="coreStore.handleImageLoadError"
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AlternativeAccommodationCardContent
                :hotel="accommodationItem"
                :ingredient-index="props.segment.context.ingredientIndex"
                @show-details="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: false,
                  })
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: true,
                  })
                "
              />
            </template>
            <template #footer>
              <div class="footer">
                <AccommodationPrice :price="accommodationItem.deltaPrice" />
                <AppButton
                  v-if="!props.isMetaExperiment"
                  type="primary"
                  :left-icon="{ name: 'change' }"
                  state="enabled"
                  size="small"
                  @click="
                    showDetails(accommodationItem, {
                      isAlternative: true,
                      shouldScrollToRooms: false,
                    }),
                      statsigEventsStore.logTripOverviewChangeAlternateHotelCardClicked()
                  "
                >
                  {{ $t('overview_accommodation_footer.change') }}
                </AppButton>
              </div>
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>
        </button>

        <button
          v-if="accommodationItem === null && !props.printMode"
          class="card generic"
          @click="openHotelSelection()"
        >
          <span class="generic-card-title">
            {{ $t('planner.alternative_hotels.generic_card_title') }}
          </span>
          <AppButton type="secondary" size="medium" state="enabled">
            {{ $t('planner.alternative_hotels.change_hotel') }}
          </AppButton>
          <SemboStarIllustration class="star-illustration" />
          <SemboHeartIllustration class="heart-illustration" />
        </button>
      </template>
    </AppArrowScroller>
    <slot />
  </div>
  <DisneylandCampaignBanner
    v-if="disneylandCampaignBannerData && shouldShowDisneylandCampaignBanner"
    @open-modal="emit('open-modal')"
  >
    <template #title>
      {{ disneylandCampaignBannerData.title }}
    </template>
    <template #content>
      {{ disneylandCampaignBannerData.content }}
    </template>
  </DisneylandCampaignBanner>
</template>

<style scoped lang="scss">
.accommodation-products-carousel-desktop {
  --card-width: 56vw;

  @include for-less-or-equal-than-1199-px {
    --card-width: calc(90vw - 60px);
  }

  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  background-color: var(--color-off-white);

  &.print-mode {
    background-color: var(--color-white);
    --card-width: 100%;

    .card {
      cursor: default;
    }
  }

  .card {
    all: unset;
    display: grid;
    grid-auto-rows: 1fr;
    width: var(--card-width);
    height: 100%;
    min-width: var(--card-width);
    margin-right: var(--spacing-12);
    cursor: pointer;

    .footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: var(--spacing-12) 0 0 0;
      flex-wrap: wrap;

      .label {
        @include font-label-m-bold;
        line-height: var(--line-heights-14);
        padding: var(--spacing-8) var(--spacing-12);
        border-radius: var(--spacing-8);
        background-color: var(--color-brand-lilac);
      }
    }

    &.generic {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-24);
      background-color: var(--color-neutral-10);
      width: var(--card-width);
      border-radius: var(--border-radius-8);
      margin-right: 0;
      overflow: hidden;
      height: 100%;

      @include on-hover {
        cursor: default;
      }

      .generic-card-title {
        color: var(--color-soft-black);
        @include font-label-m-bold;
        line-height: var(--line-heights-16);
        text-align: center;
      }

      .heart-illustration {
        position: absolute;
        top: 9px;
        left: 40px;
      }
      .star-illustration {
        position: absolute;
        bottom: -18px;
        right: 25px;
      }
    }
  }
}
</style>
